import {isFeatureEnabled} from '@github-ui/feature-flags'
import {applyOperation} from '@github-ui/optimistic-connection'
import {useQueryClient} from '@github-ui/react-query'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {getHighlightedEvent, isEventHighlighted} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {timelineItemKey} from '@github-ui/timeline-items/TimelineItemKey'
import {VALUES} from '@github-ui/timeline-items/Values'
import {useAlive} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import {Timeline} from '@primer/react'
import {type ReactNode, type RefObject, useCallback, useEffect, useId, useMemo, useRef} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {useSafariTimelineScrollObserver} from '../../hooks/use-safari-scroll-preservation'
import {useNewScrollToHighlighted} from '../../hooks/use-scroll-to-highlighted'
import {invalidateScopes, scheduleCanonicalTimelineUpdate} from '../secondary-view-invalidation'
import {CrawlerPagination} from './CrawlerPagination'
import {
  TIMELINE_CONNECTION_PATH,
  type TimelineFeed,
  type TimelineIssue,
  timelineItemsQueryKeyPrefix,
  timelineReadIncludes,
} from './fetch-timeline-items'
import {IssueLinkPreheat} from './IssueLinkPreheat'
import type {TimelineItem} from './IssueTimeline'
import type {EventProps} from './IssueTimelineItem'
import {readsInclude, withCountDelta} from './timeline-write-overlay'
import {TimelineTransferringFlash} from './TimelineTransferringFlash'
import {useFocusAfterTimelineLoad} from './use-focus-after-timeline-load'
import {useHighlightVisibility} from './use-highlight-visibility'
import {VirtualIssueTimelinePage} from './VirtualIssueTimelinePage'

const timelineStyle = {overflowAnchor: 'none' as const}

type IssueTimelineAliveUpdatesProps = {
  channel: string | null | undefined
  issueId: string
  owner: string
  repo: string
  number: number
  newestSectionQueryKey?: readonly unknown[]
  feed?: TimelineFeed
  onCanonicalTimelineUpdated?: () => void | Promise<void>
}

export const IssueTimelineAliveUpdates = ({
  channel,
  issueId,
  owner,
  repo,
  number,
  newestSectionQueryKey,
  feed = 'activity',
  onCanonicalTimelineUpdated,
}: IssueTimelineAliveUpdatesProps) => {
  const queryClient = useQueryClient()
  const isMounted = useIsMounted()
  const handleUpdate = useCallback(
    (data: {
      wait?: number
      issue_timeline_updated?: true
      comment_created_id?: string
      deleted_comment_id?: string
    }) => {
      window.setTimeout(() => {
        if (!isMounted()) return

        const deletedCommentId = data.deleted_comment_id
        if (deletedCommentId) {
          const timelineKey = timelineItemsQueryKeyPrefix(owner, repo, number, feed)
          void (async () => {
            await queryClient.cancelQueries({queryKey: timelineKey}, {silent: true})
            if (!isMounted()) return

            const cachedPages = queryClient.getQueriesData<TimelineIssue>({queryKey: timelineKey})
            if (!cachedPages.some(([, page]) => page && readsInclude([page], deletedCommentId))) return

            const operation = {
              type: 'removeFromConnection' as const,
              connectionPath: TIMELINE_CONNECTION_PATH,
              nodeId: deletedCommentId,
            }
            queryClient.setQueriesData<TimelineIssue>(
              {queryKey: timelineKey, predicate: query => timelineReadIncludes(query.queryKey, 'ISSUE_COMMENT')},
              page => {
                if (!page) return page

                return withCountDelta(applyOperation(page, issueId, operation), -1)
              },
            )
          })()
          if (feed === 'comments') {
            void queryClient.invalidateQueries({
              queryKey: timelineItemsQueryKeyPrefix(owner, repo, number, 'activity'),
              refetchType: 'none',
            })
          }
          return
        }

        if (feed === 'comments' && data.issue_timeline_updated) {
          void queryClient.invalidateQueries({
            queryKey: timelineItemsQueryKeyPrefix(owner, repo, number, 'activity'),
            refetchType: 'none',
          })
        }

        if (feed === 'comments' ? !data.comment_created_id : !data.issue_timeline_updated) return
        if (onCanonicalTimelineUpdated) {
          scheduleCanonicalTimelineUpdate({
            queryClient,
            issueId,
            coordinates: {owner, repo, number},
            timelineKeyOverride: newestSectionQueryKey?.length ? newestSectionQueryKey : undefined,
            effect: () => (isMounted() ? onCanonicalTimelineUpdated() : undefined),
          })
          return
        }
        invalidateScopes({
          queryClient,
          scopes: ['timeline'],
          issueId,
          coordinates: {owner, repo, number},
          timelineKeyOverride:
            newestSectionQueryKey && newestSectionQueryKey.length > 0
              ? newestSectionQueryKey
              : timelineItemsQueryKeyPrefix(owner, repo, number, feed),
        })
      }, data.wait ?? 0)
    },
    [feed, isMounted, issueId, newestSectionQueryKey, number, onCanonicalTimelineUpdated, owner, queryClient, repo],
  )

  useAlive(channel ?? null, handleUpdate)
  return null
}

export const IssueTimelineAliveUpdatesLoader = ({
  secondaryTimelineMode,
  ...props
}: IssueTimelineAliveUpdatesProps & {secondaryTimelineMode?: 'relay' | 'endpoint'}) =>
  secondaryTimelineMode === 'endpoint' ? <IssueTimelineAliveUpdates {...props} /> : null

type TimelinePanelProps = {
  timelineItems: TimelineItem[] | null
  totalItemCount: number
  /** The bottom section's cache key, so an appended row invalidates it alone. */
  newestSectionQueryKey?: readonly unknown[]
  onCanonicalTimelineUpdated?: () => void | Promise<void>
  highlightedEvent: string | undefined
  highlightedReady: boolean
  issueId: string
  owner: string
  repo: string
  number: number
  feed?: TimelineFeed
  repositoryId: string
  repositoryNameWithOwner: string
  issueUrl: string
  page?: number
  isTransferInProgress?: boolean
  /** Set on routes with no Relay environment: the preheat warms Relay's cache and needs one. */
  disablePreheat?: boolean
  issueBodyRef?: RefObject<HTMLDivElement | null>
  /** A visible heading that names the list, used in place of the hidden one. */
  commentsHeadingId?: string
  emptyState?: ReactNode
  /** @see SecondaryTimelineSource */
  secondaryTimelineMode?: 'relay' | 'endpoint'
} & EventProps

/**
 * Shared rendering component for timeline items. Handles:
 * - Highlight state (parse, click-to-dismiss, restore on change)
 * - Scroll-to-highlighted + Safari scroll preservation
 * - Paginating timeline items into virtual pages
 * - Link preheating for referenced issues
 * - Crawler pagination for bots
 * - Rendering `<Timeline>` with `<VirtualIssueTimelinePage>` children
 *
 * Used by ClassicTimeline.
 */
export function TimelinePanel({
  timelineItems,
  totalItemCount,
  newestSectionQueryKey,
  onCanonicalTimelineUpdated,
  highlightedEvent,
  highlightedReady,
  issueId,
  owner,
  repo,
  number,
  feed = 'activity',
  repositoryId,
  repositoryNameWithOwner,
  issueUrl,
  page,
  isTransferInProgress,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  disablePreheat,
  issueBodyRef,
  secondaryTimelineMode,
  issueUpdatesChannel,
  commentsHeadingId,
  emptyState,
}: TimelinePanelProps) {
  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])

  // Highlight borders are visible by default for a highlighted event. The click
  // handler below clears them, and the hook restores them when a new highlight id
  // arrives (e.g. the user clicks an in-page link to a different event).
  const [shouldHighlightElement, setShouldHighlightElement] = useHighlightVisibility(highlight?.id)

  // After rendering, clicking anywhere should remove the borders
  useEffect(() => {
    const handlePageClick = () => {
      setShouldHighlightElement(false)
    }
    document.addEventListener('click', handlePageClick)

    return () => {
      document.removeEventListener('click', handlePageClick)
    }
  }, [highlight, setShouldHighlightElement])

  const hasNextPage = getHasNextPage(totalItemCount, page)

  // Ref for the timeline container to gather issue links on demand
  const timelineContainerRef = useRef<HTMLDivElement>(null)

  // Iterate items to check if the loaded highlight is in the list
  // Room for performance optimization if we face issues in large timelines, as we can determine
  // if the highlighted item is already present during the mapping process.
  // https://github.com/github/github/blob/9ecd32f60f96a519f99462c48f6235d5842ca814/ui/packages/issue-viewer/components/timeline/IssueTimeline.tsx#L249
  const highlightedItemRef = useRef<HTMLLIElement>(null)
  const timelineRef = useRef<HTMLOListElement>(null)

  const isHighlightLoaded = useMemo(() => {
    if (!timelineItems) return false
    if (!shouldHighlightElement || !highlight) return false

    return timelineItems.some(item => {
      if (item.type !== 'event' || !item.item) return false

      return isEventHighlighted(item.item, highlight)
    })
  }, [highlight, timelineItems, shouldHighlightElement])

  useNewScrollToHighlighted(isHighlightLoaded, highlightedItemRef, highlightedEvent, highlightedReady)

  // Safari doesn't support CSS scroll anchoring, so we need to manually observe
  // DOM changes and preserve scroll position when timeline items are added/removed
  useSafariTimelineScrollObserver(timelineRef)

  if (!timelineItems) {
    throw new Error(
      `Missing timeline items for issue ${issueId}. Total item count: ${totalItemCount}. Highlighted event: ${highlightedEvent}`,
    )
  }

  // Restores keyboard focus after the "Load more"/"Load all" control is activated.
  // Lives here so it can observe the full timeline committing new rows, regardless
  // of which page the control was on.
  const requestFocusAfterLoad = useFocusAfterTimelineLoad(timelineItems)

  const paginatedTimelineItems = useMemo(() => {
    const pageSize = VALUES.timeline.virtualPageSize
    const pages = []
    if (timelineItems.length === 0) {
      return []
    }
    const getTimelineItemCount = (item: TimelineItem) => {
      // undefined items or load items are not counted
      if (!item || item.type !== 'event') {
        return 0 // Load items count as 1
      }
      if (!item.rollupGroup) {
        return 1 // Single event item
      }
      // If the item has a rollup group, count the number of rolled up events for each rollup type
      return Object.values(item.rollupGroup).reduce((count, rollup) => count + rollup.length, 0)
    }

    let currentPage: TimelineItem[] = []
    let currentPageItemCount = 0

    for (const timelineItem of timelineItems) {
      const itemCount = getTimelineItemCount(timelineItem)

      // Check if adding this item would exceed the page size
      if (currentPageItemCount + itemCount > pageSize && currentPage.length > 0) {
        // Add current page to pages and start a new page
        pages.push(currentPage)
        currentPage = [timelineItem]
        currentPageItemCount = itemCount
      } else {
        // Add item to current page
        currentPage.push(timelineItem)
        currentPageItemCount += itemCount
      }
    }

    // Add the last page if it has items
    if (currentPage.length > 0) {
      pages.push(currentPage)
    }

    return pages
  }, [timelineItems])

  // Under primer_react_timeline_list_semantics, <Timeline> renders as an <ol role="list">.
  // The flag-off path names the events group via <section aria-label="Events">, which the
  // flag-on path drops (a <section> cannot be an <ol> child). To keep the list identifiable
  // to screen readers under the flag, point the <ol> at the existing visually-hidden heading.
  // Both attributes are flag-gated so the flag-off DOM stays byte-for-byte unchanged.
  const useListSemantics = isFeatureEnabled('primer_react_timeline_list_semantics')

  // Unique per instance so two IssueViewers on the page (primary + side panel) do not
  // emit a duplicate heading id / aria-labelledby target.
  const headingId = useId()

  return (
    <div ref={timelineContainerRef}>
      {!disablePreheat && (
        <IssueLinkPreheat
          key={issueUrl}
          issueBodyRef={issueBodyRef}
          timelineContainerRef={timelineContainerRef}
          issueUrl={issueUrl}
          totalItemCount={totalItemCount}
        />
      )}
      {Boolean(viewer) && (
        <IssueTimelineAliveUpdatesLoader
          secondaryTimelineMode={secondaryTimelineMode}
          channel={issueUpdatesChannel}
          issueId={issueId}
          owner={owner}
          repo={repo}
          number={number}
          feed={feed}
          newestSectionQueryKey={newestSectionQueryKey}
          onCanonicalTimelineUpdated={onCanonicalTimelineUpdated}
        />
      )}
      {isTransferInProgress && <TimelineTransferringFlash />}
      {timelineItems.length === 0 && emptyState}
      {!commentsHeadingId && (
        <h2 className="sr-only" id={useListSemantics ? headingId : undefined}>
          {LABELS.timeline.header}
        </h2>
      )}
      {hasNextPage && <CrawlerPagination nextPage={page ? page + 1 : 1} canonicalUrl={ssrSafeLocation.pathname} />}
      {paginatedTimelineItems.length > 0 && (
        <Timeline
          ref={timelineRef}
          id="issue-timeline"
          data-testid={TEST_IDS.issueTimelineContainer}
          aria-labelledby={useListSemantics ? (commentsHeadingId ?? headingId) : undefined}
          style={timelineStyle}
        >
          {paginatedTimelineItems.map(pageItems => (
            <VirtualIssueTimelinePage
              key={`page-${pageItems.length}-${
                pageItems[0]?.type === 'event' && pageItems[0]?.item
                  ? `event-${timelineItemKey(pageItems[0].item)}`
                  : `load`
              }`}
              timelineItems={pageItems}
              shouldHighlightElement={shouldHighlightElement}
              highlight={highlight}
              viewer={viewer}
              issueId={issueId}
              repositoryId={repositoryId}
              repositoryNameWithOwner={repositoryNameWithOwner}
              issueUrl={issueUrl}
              showTimelineConnectorLines={feed !== 'comments'}
              onCommentChange={onCommentChange}
              onCommentReply={onCommentReply}
              onCommentEditCancel={onCommentEditCancel}
              optionConfig={optionConfig}
              highlightedItemRef={highlightedItemRef}
              secondaryTimelineMode={secondaryTimelineMode}
              requestFocusAfterLoad={requestFocusAfterLoad}
              issueUpdatesChannel={issueUpdatesChannel}
            />
          ))}
        </Timeline>
      )}
    </div>
  )
}

function getHasNextPage(totalItemCount: number, page?: number): boolean {
  if (!page) {
    // The initial load returns 30 items (15 from front and 15 from back)
    return totalItemCount > 30
  }

  // 150 is the page size (same size we use when a regular user clicks "Load more")
  return totalItemCount > 15 + page * 150
}
